import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Play, X } from "lucide-react";

const reviews = [
  {
    quote: "I've watched our wedding film over 30 times and I cry every single time. It's the most incredible thing we own. Worth every single penny.",
    couple: "Jessica & David",
    venue: "Gaynes Park, Essex",
    rating: 5,
  },
  {
    quote: "We weren't sure if we needed both a photographer and videographer but honestly it's the best decision we made. The photos are gorgeous and the film captured moments we didn't even know happened. Our families are obsessed.",
    couple: "Charlotte & James",
    venue: "Gosfield Hall, Essex",
    rating: 5,
  },
  {
    quote: "From the first call to receiving our photos and film, the whole experience was effortless. They knew exactly where to be at every moment. You'd never know they were there but they caught everything.",
    couple: "Emily & Ben",
    venue: "Braxted Park, Essex",
    rating: 5,
  },
  {
    quote: "My mum called me sobbing after watching the film. My bridesmaid group chat went mad. Even my husband, who said he'd never watch it, has seen it about 20 times.",
    couple: "Sophie & Alex",
    venue: "Leez Priory, Essex",
    rating: 5,
  },
];

const videoReviews = [
  { id: "PSC-N1gEnT8", label: "Rebecca & Dan" },
  { id: "e3q_0U9sojw", label: "Charlotte & Joe" },
  { id: "cL8cyPOL5hw", label: "Charley & Fabio" },
];

export default function ReviewsSection() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section
      data-testid="reviews-section"
      className="relative bg-[#FAF9F7] py-24 md:py-32 overflow-hidden"
      aria-label="Couple reviews and testimonials"
    >
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("https://images.pexels.com/photos/32427331/pexels-photo-32427331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Don't take our word for it
          </p>
          <h2
            data-testid="reviews-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#1A1917]"
          >
            Hear it from real Essex couples
          </h2>
        </motion.div>

        {/* Video Reviews */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16">
          {videoReviews.map((vr, i) => (
            <motion.button
              key={vr.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              onClick={() => setActiveVideo(vr.id)}
              data-testid={`video-review-${i}`}
              className="group relative aspect-video overflow-hidden border border-[#D5D0C8]/40 rounded-sm"
            >
              <img
                src={`https://img.youtube.com/vi/${vr.id}/maxresdefault.jpg`}
                alt={`${vr.label} couple review`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="play-button-overlay">
                <Play className="text-white drop-shadow-lg" fill="white" strokeWidth={0} />
              </div>
              <div className="absolute bottom-3 left-3">
                <span className="text-[10px] tracking-[0.15em] uppercase font-sans bg-[#C2A878]/90 text-[#0A0908] px-2 py-1">
                  Couple Review
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Written Reviews */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((review, i) => (
            <motion.div
              key={review.couple}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`review-card-${i}`}
              className="relative p-6 md:p-8 border border-white/[0.06] bg-white/[0.02]"
            >
              <span className="quote-mark" aria-hidden="true">"</span>
              <div className="relative">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: review.rating }).map((_, j) => (
                    <Star
                      key={j}
                      className="w-3.5 h-3.5 text-[#C2A878] fill-[#C2A878]"
                    />
                  ))}
                </div>
                <p className="text-sm md:text-base text-[#1A1917]/90 font-sans leading-relaxed italic">
                  "{review.quote}"
                </p>
                <div className="mt-4 pt-4 border-t border-[#D5D0C8]/40">
                  <p className="text-sm font-sans text-[#1A1917]">{review.couple}</p>
                  <p className="text-xs text-[#5A5750] font-sans mt-0.5">{review.venue}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {activeVideo && (
        <div
          className="video-modal-overlay"
          data-testid="review-video-modal"
          onClick={() => setActiveVideo(null)}
        >
          <button
            onClick={() => setActiveVideo(null)}
            data-testid="review-video-modal-close"
            className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
          >
            <X size={32} />
          </button>
          <div
            className="w-full max-w-4xl aspect-video mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
              title="Couple Review"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
